import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  BookOpen,
  Download,
  Calendar,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  AlertCircle,
  FileSpreadsheet,
  Calculator,
  Building,
  Users,
  ArrowRight,
  RefreshCw,
} from 'lucide-react';
import api from '../lib/api';

const AccountingEntriesPage = () => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [exporting, setExporting] = useState(false);

  const months = [
    { value: 1, label: 'Janvier' }, { value: 2, label: 'Février' },
    { value: 3, label: 'Mars' }, { value: 4, label: 'Avril' },
    { value: 5, label: 'Mai' }, { value: 6, label: 'Juin' },
    { value: 7, label: 'Juillet' }, { value: 8, label: 'Août' },
    { value: 9, label: 'Septembre' }, { value: 10, label: 'Octobre' },
    { value: 11, label: 'Novembre' }, { value: 12, label: 'Décembre' },
  ];

  const years = Array.from({ length: 3 }, (_, i) => new Date().getFullYear() - 1 + i);

  useEffect(() => {
    loadData();
  }, [selectedMonth, selectedYear]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/hr/accounting/entries', {
        params: { year: selectedYear, month: selectedMonth }
      });
      setData(response.data);
    } catch (error) {
      console.error('Error loading entries:', error);
      if (error.response?.status !== 404) {
        toast.error(t('hr.loadError') || 'Erreur de chargement');
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const response = await api.get('/hr/accounting/entries/export', {
        params: { year: selectedYear, month: selectedMonth, format: 'csv' },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ecritures_paie_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('hr.exportSuccess') || 'Export réussi');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error(error.response?.data?.detail || t('hr.exportError') || 'Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-CH', { 
      style: 'currency', 
      currency: 'CHF',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const getEntryTypeStyle = (type) => {
    switch (type) {
      case 'expense':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'liability':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'asset':
        return 'bg-green-50 text-green-700 border-green-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounting-entries-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-blue-600" />
            {t('hr.accountingEntries') || 'Écritures comptables'}
          </h1>
          <p className="text-slate-500 mt-1">
            {data?.period?.label || `${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={loadData}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh') || 'Actualiser'}
          </Button>
          <Button 
            onClick={handleExportCSV}
            disabled={!data?.entries?.length || exporting}
            className="bg-green-600 hover:bg-green-700"
            data-testid="export-csv-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            {exporting ? 'Export...' : (t('hr.exportCSV') || 'Exporter CSV')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <Select 
                value={selectedMonth.toString()} 
                onValueChange={(v) => setSelectedMonth(parseInt(v))}
              >
                <SelectTrigger className="w-[140px]" data-testid="filter-month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map((m) => (
                    <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Select 
                value={selectedYear.toString()} 
                onValueChange={(v) => setSelectedYear(parseInt(v))}
              >
                <SelectTrigger className="w-[100px]" data-testid="filter-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {data?.payslips_count > 0 && (
              <Badge variant="outline" className="bg-blue-50 text-blue-700">
                <Users className="w-3 h-3 mr-1" />
                {data.payslips_count} fiche(s) de paie
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {data?.summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{t('hr.grossSalaries') || 'Salaires bruts'}</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {formatCurrency(data.summary.gross_salaries)}
                  </p>
                </div>
                <Building className="w-8 h-8 text-slate-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{t('hr.employerCharges') || 'Charges patronales'}</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {formatCurrency(data.summary.employer_charges)}
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{t('hr.totalCost') || 'Coût total'}</p>
                  <p className="text-2xl font-bold text-red-600">
                    {formatCurrency(data.summary.total_cost)}
                  </p>
                </div>
                <Calculator className="w-8 h-8 text-red-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{t('hr.netSalaries') || 'Salaires nets'}</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(data.summary.net_salaries)}
                  </p>
                </div>
                <TrendingDown className="w-8 h-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Balance Check */}
      {data?.totals && (
        <Card className={data.totals.balanced ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {data.totals.balanced ? (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-red-600" />
                )}
                <div>
                  <p className={`font-medium ${data.totals.balanced ? 'text-green-800' : 'text-red-800'}`}>
                    {data.totals.balanced 
                      ? (t('hr.entriesBalanced') || 'Écritures équilibrées')
                      : (t('hr.entriesUnbalanced') || 'Écritures non équilibrées')
                    }
                  </p>
                  <p className={`text-sm ${data.totals.balanced ? 'text-green-600' : 'text-red-600'}`}>
                    Débit: {formatCurrency(data.totals.debit)} | Crédit: {formatCurrency(data.totals.credit)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-lg font-mono">
                <span className="text-red-600">{formatCurrency(data.totals.debit)}</span>
                <ArrowRight className="w-5 h-5 text-slate-400" />
                <span className="text-blue-600">{formatCurrency(data.totals.credit)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Journal Entries Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-600" />
            {t('hr.journalEntries') || 'Journal des écritures'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {!data?.entries?.length ? (
            <div className="p-12 text-center">
              <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {t('hr.noEntries') || 'Aucune écriture'}
              </h3>
              <p className="text-slate-500 mb-4">
                {t('hr.noValidatedPayslips') || 'Aucune fiche de paie validée pour cette période'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-[100px]">{t('hr.date') || 'Date'}</TableHead>
                  <TableHead className="w-[80px]">{t('hr.account') || 'Compte'}</TableHead>
                  <TableHead>{t('hr.accountName') || 'Libellé'}</TableHead>
                  <TableHead>{t('hr.description') || 'Description'}</TableHead>
                  <TableHead className="text-right w-[120px]">{t('hr.debit') || 'Débit'}</TableHead>
                  <TableHead className="text-right w-[120px]">{t('hr.credit') || 'Crédit'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.entries.map((entry, index) => (
                  <TableRow key={index} className={getEntryTypeStyle(entry.type)}>
                    <TableCell className="font-mono text-sm">{entry.date}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">
                        {entry.account}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">{entry.account_name}</TableCell>
                    <TableCell className="text-slate-600">{entry.description}</TableCell>
                    <TableCell className="text-right font-mono">
                      {entry.debit > 0 && (
                        <span className="text-red-600 font-medium">
                          {formatCurrency(entry.debit)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {entry.credit > 0 && (
                        <span className="text-blue-600 font-medium">
                          {formatCurrency(entry.credit)}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals row */}
                <TableRow className="bg-slate-100 font-bold border-t-2">
                  <TableCell colSpan={4} className="text-right">
                    {t('hr.totals') || 'TOTAUX'}
                  </TableCell>
                  <TableCell className="text-right font-mono text-red-700">
                    {formatCurrency(data.totals.debit)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-blue-700">
                    {formatCurrency(data.totals.credit)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Account Legend */}
      {data?.accounts && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">
              {t('hr.accountLegend') || 'Plan comptable utilisé'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="font-medium text-red-700 mb-2">Charges (Classe 5)</p>
                <ul className="space-y-1 text-slate-600">
                  <li>5200 - Salaires et traitements</li>
                  <li>5201 - 13ème salaire</li>
                  <li>57xx - Charges sociales employeur</li>
                </ul>
              </div>
              <div>
                <p className="font-medium text-blue-700 mb-2">Passifs (Classe 2)</p>
                <ul className="space-y-1 text-slate-600">
                  <li>227x - Cotisations sociales à payer</li>
                  <li>2279 - Salaires nets à payer</li>
                </ul>
              </div>
              <div>
                <p className="font-medium text-green-700 mb-2">Actifs (Classe 1)</p>
                <ul className="space-y-1 text-slate-600">
                  <li>1020 - Banque</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AccountingEntriesPage;
